import type { Issue } from '../../../../api/DigiobcanApi';
import { getReportStatusMeta } from '../../../../components/AdminReportStatusItem';
import {
  adminCrumbs,
  ADMIN_SECTION,
} from '../../../../components/Layout/crumbs';
import NotFound from '../../../../components/NotFound';
import { adminGetReportDetail } from '../../../../features/queries/adminGetReportDetail';
import { adminGetReportIssues } from '../../../../features/queries/adminGetReportIssues';
import { useCanManageReportCategory } from '../../../../features/queries/meGetReportCategories';
import { entityId, shortId } from '../../../../lib/ids';
import { LIGHTBOX_STYLES } from '../../../../lib/lightbox';
import { listPathWithSearch } from '../../../../lib/listSearch';
import { useQ } from '../../../../lib/query';
import { useLayout } from '../../../../providers/LayoutProvider';
import { useResponsive } from '../../../../providers/ResponsiveProvider';
import { __, isPreviewableMedia } from '../../../../utils';
import IssueDetail from './IssueDetail';
import IssueList from './IssueList';
import ReportArchive from './ReportArchive';
import ReportEnvelope, { ENVELOPE_COLUMN_WIDTH } from './ReportEnvelope';
import ReportTiles from './ReportTiles';
import ReportVisibilityToggle from './ReportVisibilityToggle';
import {
  Box,
  EmptyState,
  Skeleton,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import LightBox from 'yet-another-react-lightbox';
import VideoPlugin from 'yet-another-react-lightbox/plugins/video';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';

/**
 * Poradie kariet nálezov: čo čaká na úradníka, čo má rozrobené, čo dokončil,
 * a až potom to, čo je len história. API ich vracia v poradí vzniku - v tom,
 * v akom ich vyrobil `reports-processor`, teda náhodnom z pohľadu toho, kto
 * ich rieši.
 */
const STATUS_ORDER: Record<Issue['status'], number> = {
  open: 0,
  accepted: 1,
  resolved: 2,
  duplicate: 3,
  moved: 4,
};

/**
 * Stavy, ktorých karty sú po otvorení stránky rozbalené - to, čo úradník má
 * na stole. Vyriešené, duplikáty a presunuté sú hotové veci: patria na
 * obrazovku, lebo z nich vidno, ako sa podnet rieši inde, ale nemusia zaberať
 * výšku. Rozbaliť sa dajú klikom na hlavičku karty.
 */
const EXPANDED_STATUSES: Issue['status'][] = ['open', 'accepted'];

/**
 * Správa podnetu.
 *
 * Podnet je **obálka**; rieši sa na úrovni nálezov, jeden na kategóriu.
 * Na desktope stojí obálka ako karta vľavo a nálezy sú **karty pod sebou**
 * vpravo - všetky rozbalené, aj s vyjadreniami. Mobil má dve obrazovky za
 * sebou: obálka so zoznamom nálezov, a detail nálezu.
 *
 * Dvojstĺpec zoznam | detail, ktorý tu bol dovtedy, zmizol. Prepínanie nálezov
 * bolo trenie bez úžitku: kategórií má podnet spravidla jednu až tri, obe
 * polovice sa museli skrolovať zvlášť a to, čo úradník naozaj potrebuje -
 * porovnať, čo ktorá kategória odpovedala - sa nedalo vidieť naraz.
 *
 * Adresa `…/nalezy/:issueId` zostáva. Na mobile je to samostatný krok
 * navigácie, na desktope odkaz na konkrétny nález: stránka naň odroluje
 * a karta sa na chvíľu zvýrazní.
 */
const ReportDetailAdminPage: React.FC = () => {
  const { reportId: reportIdParam, issueId: issueIdParam } = useParams<{
    reportId: string;
    issueId?: string;
  }>();
  // Adresa nesie holý koncový segment, appka a API appkovú podobu - preklad
  // je tu, aby ho `IssueList`, `IssueDetail` ani dotazy nemuseli riešiť.
  const reportId = entityId(reportIdParam, 'report');
  const issueId = entityId(issueIdParam, 'issue');
  const navigate = useNavigate();
  const { isMd, isLg } = useResponsive();
  const { setPageTitle, setFooter, setBackPath, setActions, setBreadcrumbs } =
    useLayout();

  /**
   * Cesta na zoznam aj s výberom, s ktorým z neho úradník odišiel - filtre,
   * hľadanie, zoradenie aj strana žijú v jeho adrese a holá cesta ich zahodí.
   * Používa ju šípka späť, článok cesty aj stránka „nenašlo sa"; keď sa detail
   * otvoril priamym odkazom (napr. z notifikácie), je to holá cesta zoznamu.
   */
  const reportsPath = listPathWithSearch(ADMIN_SECTION.reports.href);

  const [previewMedia, setPreviewMedia] = React.useState<number>();

  const {
    data: report,
    isLoading: isLoadingReport,
    isError,
    refetch: refetchReport,
  } = useQ(adminGetReportDetail, { reportId });

  const {
    data: issues = [],
    isLoading: isLoadingIssues,
    refetch: refetchIssues,
  } = useQ(adminGetReportIssues, { reportId });

  /**
   * Karty nálezov bez presunutých, ktoré si kategória medzitým vzala späť.
   *
   * Presunutý nález je história a kým v jeho kategórii nič iné nie je, je to
   * jediná stopa po podnete - vedie z neho „Znovu prijať nález", takže musí
   * byť vidieť. Len čo v tej istej kategórii nález zase je, sú pod sebou dve
   * karty s tým istým názvom a tá staršia nehovorí nič než „takto to bolo
   * predtým". Medzi karty preto nepatrí; z adresy sa naň dostať naďalej dá.
   *
   * Poradie určuje `STATUS_ORDER`, v rámci rovnakého stavu dátum vzniku - aby
   * sa karty pri každom načítaní neprehadzovali.
   */
  const visibleIssues = React.useMemo(() => {
    const live = new Set(
      issues
        .filter((issue) => issue.status !== 'moved')
        .map((issue) => issue.category_id),
    );

    return issues
      .filter(
        (issue) => issue.status !== 'moved' || !live.has(issue.category_id),
      )
      .sort(
        (a, b) =>
          STATUS_ORDER[a.status] - STATUS_ORDER[b.status] ||
          a.inserted_at.localeCompare(b.inserted_at),
      );
  }, [issues]);

  /**
   * Ktoré nálezy úradník len vidí a ktoré aj rieši.
   *
   * Od septembra 2026 sa to rozchádza: `pruser:report-categories:read-all`
   * (sekretariáty) otvára celý zoznam podnetov na čítanie, ale zápis zostáva
   * na kategóriách, ku ktorým je úradník priradený. Rozhodovanie o náleze
   * vypína `unmanaged`, akcie nad **celým podnetom** (archivácia, „nevhodné",
   * prílohy) stačí jedna vlastná kategória - preto `some` a nie `every`.
   *
   * Kým sa nálezy načítavajú, je podnet „riešiteľný": prázdne pole by inak
   * na okamih zhaslo tlačidlá aj tomu, kto na ne právo má.
   */
  const canManageCategory = useCanManageReportCategory();
  const reportReadOnly =
    issues.length > 0 &&
    !issues.some((issue) => canManageCategory(issue.category_id));

  const selected = issues.find((issue) => issue.id === issueId);

  // Nad definíciou efektov: prepínač nevhodnosti v hlavičke ho má
  // v závislostiach a v tele komponentu sa na `const` pred jeho riadkom
  // nedá siahnuť.
  const refetchAll = React.useCallback(async () => {
    await Promise.all([refetchReport(), refetchIssues()]);
  }, [refetchReport, refetchIssues]);

  // Titul hlavičky nesie identifikátor podnetu vo **všetkých** rozloženiach.
  // Názov tam na mobile chvíľu stál, aby mal pred sebou šípku späť, ale
  // hlavička ho skracuje trojbodkou (`nowrap` + `ellipsis`) a podnet sa tým dal
  // na mobile prečítať len po prvých pár slov. Názov teda patrí do obsahu,
  // hlavičke identifikátor - krátky, jednoznačný a je to to, čím sa podnet
  // menuje v notifikáciách aj medzi kolegami.
  //
  // `pathname` **musí** byť v závislostiach: `RouteMetadata` prepisuje titul
  // v `useLayoutEffect` a ten sa spúšťa pri každej zmene adresy - a výber
  // nálezu žije v adrese. Kým tu `pathname` nebol, prvé prepnutie nálezu
  // vrátilo titul na hodnotu z route a v hlavičke to vyzeralo, že sa sám
  // zmenil. Efekt stránky beží po ňom (pasívne efekty idú po layout efektoch),
  // takže takto vyhrá vždy.
  const { pathname } = useLocation();

  React.useEffect(() => {
    setPageTitle(`${__('Správa podnetu')}: ${shortId(reportId)}`);
  }, [setPageTitle, reportId, pathname]);

  // Pätičku shellu neplníme - akcia patrí k vybranému nálezu a stojí v jeho
  // paneli, nie pod celou stránkou.
  React.useEffect(() => {
    setFooter(null);
  }, [setFooter]);

  // Stav podnetu v hlavičke **nie je**. Je to agregát zo stavov nálezov a nič
  // sa podľa neho nerobí - riešenie beží na nálezoch a každý z nich nesie svoj
  // vlastný stav v zozname vedľa. Vedľa titulu z neho bola len ďalšia značka,
  // ktorá vyzerala ako stav celého podnetu, hoci taký stav neexistuje.

  // Vpravo v hlavičke stoja tri veci, ktoré platia pre **podnet ako celok**:
  // lehota, hlasy a prepínač nevhodnosti. Nie sú to akcie v obvyklom zmysle -
  // slot pre ne je jediné miesto na obrazovke, ktoré patrí celému podnetu.
  // Pod hlavičkou je už všetko buď obálka (čo napísal občan), alebo nález
  // (čo s tým robí úrad), a v oboch tie tri údaje pôsobili ako ich súčasť.
  //
  // Len na desktope: v mobilnej hlavičke by stáli v jednom riadku s titulom.
  // Tam ich nesie obálka, ktorá je celá stránka.
  //
  // Odkaz „Verejný detail" tu bol kedysi a je preč zámerne: úrad má v tejto
  // administrácii všetko, čo o podnete vidí občan, a ešte viac.
  React.useEffect(() => {
    if (!report || !isMd) {
      setActions([]);
      return;
    }

    setActions([
      {
        children: (
          <Stack direction='row' alignItems='center' gap={1}>
            <ReportTiles report={report} dense />
            <ReportVisibilityToggle
              report={report}
              onSuccess={refetchAll}
              dense
              readOnly={reportReadOnly}
            />
          </Stack>
        ),
      },
    ]);

    return () => setActions([]);
    // `pathname` z rovnakého dôvodu ako pri titule vyššie: `RouteMetadata`
    // pri zmene adresy akcie vyčistí a adresa sa v tejto stránke mení sama
    // (výber nálezu žije v nej), takže by blok ticho zmizol.
    // `reportReadOnly` tu musí byť: nálezy dobiehajú až po obálke, takže
    // prepínač v hlavičke by inak zostal na hodnote z prvého behu - aktívny
    // aj úradníkovi, ktorý podnet len vidí.
  }, [setActions, report, refetchAll, isMd, pathname, reportReadOnly]);

  // Šípka späť z detailu nálezu vedie na obálku, nie na zoznam podnetov: na
  // mobile je to krok, ktorý používateľ naozaj urobil. Route to určiť nevie -
  // `backPath` na `RouteMetadata` je statický a cieľ závisí od `reportId`.
  React.useEffect(() => {
    setBackPath(
      issueId && !isMd
        ? `/administracia/podnety/${shortId(reportId)}`
        : reportsPath,
    );
  }, [setBackPath, issueId, isMd, reportId, reportsPath]);

  // Cesta z route (`ADMIN_CRUMBS.reportDetail`) vedie na holý zoznam, takže by
  // článok „Správa podnetov" filtre zahodil rovnako ako šípka späť. Efekt
  // stránky beží po `useLayoutEffect`-e `RouteMetadata`, takže prepíše jeho
  // cestu; `pathname` v závislostiach z toho istého dôvodu ako pri titule.
  React.useEffect(() => {
    setBreadcrumbs(
      adminCrumbs(
        { ...ADMIN_SECTION.reports, href: reportsPath },
        { label: __('Detail podnetu') },
      ),
    );
  }, [setBreadcrumbs, reportsPath, pathname]);

  /**
   * Odkaz na konkrétny nález na desktope: karty sú všetky na obrazovke, takže
   * niet čo „vybrať" - stránka na tú kartu odroluje.
   *
   * Beží až po tom, čo sa nálezy načítajú (`issues` v závislostiach), inak by
   * `ref` ešte neexistoval. `issueId` z adresy sa nezahadzuje - odkaz
   * z notifikácie tak zostáva platný aj po znovunačítaní stránky.
   */
  const issueRefs = React.useRef(new Map<string, HTMLDivElement>());

  React.useEffect(() => {
    if (!isMd || !issueId) return;
    issueRefs.current
      .get(issueId)
      ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [isMd, issueId, issues]);

  if (isError) {
    return (
      <NotFound
        message={__('Podnet sa nenašlo')}
        backText={__('Späť na podnety')}
        backUrl={reportsPath}
      />
    );
  }

  // Prílohou môže byť čokoľvek - zvuk, PDF, dokument - a náhľad vie len obrázok
  // a video. Ostatné otvára `EnvelopeAttachments` v novej karte, takže do
  // `slides` nepatria a index sa počíta v tomto zúženom zozname.
  const previewable = (report?.media ?? []).filter((media) =>
    isPreviewableMedia(media.media_type),
  );

  const openPreview = (media: (typeof previewable)[number]) => {
    const index = previewable.findIndex(
      (item) => item.media_id === media.media_id,
    );
    if (index >= 0) setPreviewMedia(index);
  };

  const lightbox = (
    <LightBox
      index={previewMedia ?? 0}
      open={previewMedia !== undefined}
      close={() => setPreviewMedia(undefined)}
      slides={previewable.map((media) =>
        media.media_type.startsWith('image/')
          ? { src: media.public_url }
          : {
              type: 'video' as const,
              sources: [{ src: media.public_url, type: media.media_type }],
            },
      )}
      plugins={[Zoom, VideoPlugin]}
      styles={LIGHTBOX_STYLES}
    />
  );

  if (isLoadingReport || !report) {
    return (
      <Stack gap={1.5}>
        <Skeleton variant='rounded' height={260} />
        <Skeleton variant='rounded' height={420} />
      </Stack>
    );
  }

  const archived = !!report.archived_at;

  /**
   * Pás „archivovaný podnet" stojí nad obsahom na **každej** vetve tejto
   * stránky vrátane mobilného detailu nálezu: tam obálka nie je vidieť, takže
   * bez neho by úradník videl len nález s vypnutými prepínačmi a nemal by ako
   * zistiť prečo. Pri nearchivovanom podnete komponent nevykreslí nič.
   */
  const archiveBanner = (
    <ReportArchive
      report={report}
      variant='banner'
      onDone={refetchAll}
      onArchived={() => navigate(reportsPath)}
      readOnly={reportReadOnly}
    />
  );

  /**
   * Archivácia patrí **do** obálky, pod mapu, nie pod kartu. Kam presne, si
   * rozhoduje obálka sama - v úzkom stĺpci je mapa posledný blok, na mobile za
   * ňou stoja ešte prílohy. Slotom to je preto, že komponent potrebuje
   * oprávnenie aj odchod na zoznam a ani jedno obálka nepozná.
   */
  const archiveAction = (
    <ReportArchive
      report={report}
      variant='action'
      onDone={refetchAll}
      onArchived={() => navigate(reportsPath)}
      readOnly={reportReadOnly}
    />
  );

  // ---- mobil: obálka + zoznam, alebo detail nálezu --------------------------
  if (!isMd) {
    if (selected) {
      const meta = getReportStatusMeta(selected.status);

      return (
        <Stack sx={{ flex: 1, minHeight: 0 }}>
          <Box sx={{ px: 1.75, pt: 1.75 }}>{archiveBanner}</Box>
          {/* Identita nálezu stojí v obsahu a nie v `PageHeader`-i: hlavičku
              plní shell z `RouteMetadata` a dvojriadkový titul s chipom
              a identifikátorom doň neprepošleme. Šípka späť z hlavičky vedie
              na obálku, čo je to podstatné. */}
          <Stack gap={0.5} sx={{ px: 1.75, pt: 1.75 }}>
            <Stack
              direction='row'
              alignItems='center'
              gap={1.25}
              flexWrap='wrap'
            >
              <Typography variant='h6' fontWeight={700}>
                {selected.category_name}
              </Typography>
              <StatusChip tone={meta.tone}>{meta.title}</StatusChip>
            </Stack>
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
            >
              {selected.id}
            </Typography>
          </Stack>

          <IssueDetail
            issue={selected}
            siblings={issues}
            onDone={refetchAll}
            archived={archived}
            unmanaged={!canManageCategory(selected.category_id)}
            compact
          />
        </Stack>
      );
    }

    return (
      <>
        {/* Bez záporných marginov: na mobile dáva shell obsahu **nulový**
            padding (`Layout` má `padding: mobile ? 0 : …`), takže `mx: -1.75`
            netlačilo obsah do okrajov, ale 14 px za ne - a stránka sa dala
            skrolovať vodorovne. Bočné odsadenie si nesie každý blok sám. */}
        <Stack>
          <Box sx={{ px: 1.75, pt: 1.75 }}>{archiveBanner}</Box>
          <ReportEnvelope
            report={report}
            onChange={refetchAll}
            onPreview={openPreview}
            variant='mobile'
            archiveAction={archiveAction}
            readOnly={reportReadOnly}
          />
          {isLoadingIssues ? (
            <Skeleton variant='rounded' height={180} sx={{ m: 1.75 }} />
          ) : (
            <IssueList
              issues={visibleIssues}
              onSelect={(id) =>
                navigate(
                  `/administracia/podnety/${shortId(reportId)}/nalezy/${shortId(id)}`,
                )
              }
            />
          )}
        </Stack>
        {lightbox}
      </>
    );
  }

  // ---- desktop: obálka vľavo, nálezy ako karty vpravo -----------------------
  //
  // Nálezy nie sú dvojstĺpec ani panel s vlastnou hlavičkou: sú to karty pod
  // sebou. Každá je celý nález aj s vyjadreniami, takže úradník vidí naraz, čo
  // ktorá kategória odpovedala - a to je práve tá otázka, pre ktorú sa na túto
  // obrazovku chodí.
  //
  // **Skroluje celá stránka, nič vo vnútri.** Kým to bol dvojstĺpec, mal každý
  // panel vlastný skrolovací obal a obrazovka ako celok stála; s kartami by to
  // znamenalo toľko skrolovacích plôch, koľko je stĺpcov, a koliesko myši by
  // robilo niečo iné podľa toho, nad čím práve stojí. Preto tu nie je ani
  // `height: 100%`, ani `overflow` - obe strany sú tak vysoké, aké sú, a plocha
  // stránky rastie s nimi.
  const issuesColumn = isLoadingIssues ? (
    <Skeleton variant='rounded' height={320} />
  ) : visibleIssues.length ? (
    <Stack gap={1.5}>
      {visibleIssues.map((issue) => (
        <Box
          key={issue.id}
          ref={(node: HTMLDivElement | null) => {
            if (node) issueRefs.current.set(issue.id, node);
            else issueRefs.current.delete(issue.id);
          }}
          // Odkaz na konkrétny nález ho po odrolovaní aj označí - inak by sa
          // obrazovka len posunula a nebolo by jasné, na čo.
          sx={{
            borderRadius: 2,
            ...(issue.id === issueId && {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: '2px',
            }),
          }}
        >
          <IssueDetail
            issue={issue}
            siblings={issues}
            onDone={refetchAll}
            archived={archived}
            unmanaged={!canManageCategory(issue.category_id)}
            // Nález, na ktorý vedie adresa, je rozbalený vždy - odkaz
            // z notifikácie má viesť na obsah, nie na zbalený pruh.
            //
            // Jediná karta na obrazovke je tiež rozbalená vždy, nech je
            // v akomkoľvek stave: zbaliť sa oplatí len to, čo si konkuruje
            // o výšku s ďalšími kartami. Sám nad prázdnou stránkou je zbalený
            // pruh len klik navyše k jedinému obsahu, ktorý tam je.
            defaultOpen={
              visibleIssues.length === 1 ||
              EXPANDED_STATUSES.includes(issue.status) ||
              issue.id === issueId
            }
          />
        </Box>
      ))}
    </Stack>
  ) : (
    <EmptyState
      title={__('Podnet nemá žiadne nálezy')}
      description={__(
        'Buď ešte neprešlo spracovaním, alebo sú všetky jeho nálezy v kategóriách, na ktoré nemáte právo.',
      )}
    />
  );

  return (
    <>
      <Stack gap={1.5}>
        {archiveBanner}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isLg
              ? `minmax(0, ${ENVELOPE_COLUMN_WIDTH}px) minmax(0, 1fr)`
              : 'minmax(0, 1fr)',
            gap: 1.5,
            // `start`, nie `stretch`: obálka nemá dôvod naťahovať sa na výšku
            // nálezov, keď stránka skroluje ako celok.
            alignItems: 'start',
          }}
        >
          <ReportEnvelope
            report={report}
            onChange={refetchAll}
            onPreview={openPreview}
            // Pod 1200 px by vedľa obálky zostalo na kartu nálezu ~310 px, kde sa
            // rozhodnutie s prepínačmi a vyjadrením nedá vypĺňať. Obálka tam preto
            // ide na celú šírku nad nálezy a telo si rozloží do dvoch stĺpcov.
            variant={isLg ? 'column' : 'wide'}
            archiveAction={archiveAction}
            readOnly={reportReadOnly}
          />
          {issuesColumn}
        </Box>
      </Stack>

      {lightbox}
    </>
  );
};

export default ReportDetailAdminPage;
